import { LitElement, css, html } from 'lit';
import { shared } from './shared.ts';
import { isTap, type PointerMark } from './tap.ts';

type SkinViewer = import('skinview3d').SkinViewer;
type Three = typeof import('three');

const PARTS = ['head', 'body', 'rightArm', 'leftArm', 'rightLeg', 'leftLeg'] as const;

/**
 * Lit adapter around skinview3d. WebGL code loads only when the element nears the viewport,
 * animation pauses offscreen and under reduced motion, and everything is disposed on disconnect.
 * Skins come from our own release assets — never fetched by real account name.
 */
export class KaSkinViewer extends LitElement {
  static override properties = {
    skin: {},
    still: {},
    model: {},
    label: {},
    pickable: { type: Boolean },
    failed: { state: true },
  };
  declare skin: string;
  declare still: string;
  declare model: 'classic' | 'slim';
  declare label: string;
  /** Taps on the figure fire `ka-skin-pick` with the texture pixel {x, y} (Skin Studio). */
  declare pickable: boolean;
  /** True when WebGL is unavailable; the still image is shown instead. */
  declare failed: boolean;

  private viewer?: SkinViewer;
  private io?: IntersectionObserver;
  private ro?: ResizeObserver;
  private motion = matchMedia('(prefers-reduced-motion: reduce)');
  private three?: Three;
  private down?: PointerMark;
  private lastAzimuth = 0;
  private turned = 0;

  static override styles = [
    shared,
    css`
      :host {
        display: block;
        max-width: 320px;
      }
      .stage {
        aspect-ratio: 3 / 4;
        max-width: 320px;
        background: radial-gradient(var(--ka-stone-700), var(--ka-stone-900));
        border: 2px solid var(--ka-edge);
        box-shadow: var(--ka-bevel);
        display: grid;
        place-items: center;
      }
      canvas {
        width: 100%;
        height: 100%;
        touch-action: pan-y;
      }
      img {
        max-width: 100%;
        image-rendering: pixelated;
      }
    `,
  ];

  constructor() {
    super();
    this.skin = '';
    this.still = '';
    this.model = 'classic';
    this.label = 'Character model';
    this.pickable = false;
    this.failed = false;
  }

  override updated(changed: Map<string, unknown>) {
    // Live preview (Skin Studio): reload the texture or model in place.
    if (this.viewer && (changed.has('skin') || changed.has('model')) && this.skin)
      void this.viewer.loadSkin(this.skin, { model: this.model === 'slim' ? 'slim' : 'default' });
    else if (!this.viewer && !this.io && this.skin) this.observe();
  }

  private observe() {
    this.io = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) void this.start();
      if (this.viewer) this.viewer.renderPaused = !entry?.isIntersecting;
    });
    this.io.observe(this);
  }

  override firstUpdated() {
    if (this.skin) this.observe();
  }

  private async start() {
    if (this.viewer || this.failed) return;
    const canvas = this.renderRoot.querySelector('canvas');
    if (!canvas) return;
    try {
      const [{ SkinViewer, IdleAnimation }, three] = await Promise.all([
        import('skinview3d'),
        import('three'),
      ]);
      if (!this.isConnected) return;
      this.three = three;
      // Fit the box it sits in (at most 300×400), so narrow phone layouts don't overflow.
      const fit = () => Math.min(300, Math.floor(this.clientWidth) || 300);
      const width = fit();
      this.viewer = new SkinViewer({
        canvas,
        width,
        height: Math.round((width * 4) / 3),
        skin: this.skin,
        model: this.model === 'slim' ? 'slim' : 'default',
      });
      this.viewer.controls.enableZoom = false;
      // Follow later size changes (a phone turned sideways).
      this.ro = new ResizeObserver(() => {
        const w = fit();
        if (this.viewer && w !== this.viewer.width) this.viewer.setSize(w, Math.round((w * 4) / 3));
      });
      this.ro.observe(this);
      this.lastAzimuth = this.viewer.controls.getAzimuthalAngle();
      this.viewer.controls.addEventListener('change', this.onOrbit);
      if (!this.motion.matches) this.viewer.animation = new IdleAnimation();
    } catch {
      this.failed = true;
    }
  }

  /** Spins the figure half a turn (a button for touch, keyboard and no-drag users). */
  turnAround() {
    const p = this.viewer?.playerObject;
    if (p) p.rotation.y += Math.PI;
    this.fireTurned();
  }

  private fireTurned() {
    this.turned = 0;
    this.dispatchEvent(new CustomEvent('ka-skin-turned', { bubbles: true, composed: true }));
  }

  /** Dragging orbits the camera: add up how far, and report every quarter turn. */
  private onOrbit = () => {
    const a = this.viewer!.controls.getAzimuthalAngle();
    let d = a - this.lastAzimuth;
    d = Math.atan2(Math.sin(d), Math.cos(d));
    this.lastAzimuth = a;
    this.turned += Math.abs(d);
    if (this.turned >= Math.PI / 2) this.fireTurned();
  };

  private pointerDown(e: PointerEvent) {
    this.down = { x: e.clientX, y: e.clientY, t: e.timeStamp };
  }

  /** A tap (not a drag) on the inner layer: report which texture pixel was hit. */
  private pointerUp(e: PointerEvent) {
    const down = this.down;
    this.down = undefined;
    const v = this.viewer;
    const t = this.three;
    if (!this.pickable || !down || !v || !t) return;
    if (!isTap(down, { x: e.clientX, y: e.clientY, t: e.timeStamp })) return;
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const ndc = new t.Vector2(
      ((e.clientX - r.left) / r.width) * 2 - 1,
      -((e.clientY - r.top) / r.height) * 2 + 1,
    );
    const ray = new t.Raycaster();
    ray.setFromCamera(ndc, v.camera);
    const uv = ray.intersectObjects(
      PARTS.map((p) => v.playerObject.skin[p].innerLayer),
      true,
    )[0]?.uv;
    if (!uv) return;
    const x = Math.min(63, Math.floor(uv.x * 64));
    const y = Math.min(63, Math.floor((1 - uv.y) * 64));
    this.dispatchEvent(
      new CustomEvent('ka-skin-pick', { detail: { x, y }, bubbles: true, composed: true }),
    );
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.io?.disconnect();
    this.ro?.disconnect();
    this.viewer?.dispose();
    this.viewer = undefined;
  }

  override render() {
    if (!this.skin || this.failed) {
      return html`<div class="stage" role="img" aria-label=${this.label}>
        ${this.still ? html`<img src=${this.still} alt=${this.label} />` : html`<slot></slot>`}
      </div>`;
    }
    return html`<div class="stage">
      <canvas
        role="img"
        aria-label="${this.label}. Drag to rotate."
        @pointerdown=${this.pointerDown}
        @pointerup=${this.pointerUp}
      ></canvas>
    </div>`;
  }
}
customElements.define('ka-skin-viewer', KaSkinViewer);
